// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { Store } from "../Store";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
/**
 * Store for PlanCalendarNudge dismissal state.
 *
 * Dismissal eligibility is decided once per browser page load and then fixed for that load, so the nudge's
 * visibility never flips mid-session as time elapses:
 * - In-memory (per browser page load): the set of plans whose nudge is dismissed for the current load. It is
 *   seeded at hydrate from persisted records still within the window, augmented when the user dismisses, and
 *   cleared on reload. `getIsDismissed` reads only this set.
 * - Persisted (localStorage): timestamped dismissal records carry state across page loads. On each load, a
 *   record is treated as dismissed only if it is within the configurable
 *   evoPersistenceSettings.planCalendarNudgeDismissalDurationMs window; once the window has elapsed, the plan
 *   becomes eligible again on the next load.
 */ export class PlanCalendarNudgeStore extends EventPublishingStore {
    get name() {
        return Store.PlanCalendarNudge;
    }
    getIsDismissed(planId) {
        // Dismissal is decided once per page load (seeded from persisted records at hydrate and augmented on
        // dismiss), so visibility stays stable for the whole load and never flips mid-session as time elapses.
        return this.sessionDismissedPlanIds.has(planId);
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.DismissPlanCalendarNudgeAction:
                storeChanged = this.dismissPlan(action.planId);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                break;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    dismissPlan(planId) {
        // Refresh the persisted dismissal timestamp so the cross-load window restarts from now.
        this.upsertPersistedDismissal(planId);
        // The in-memory layer determines whether this is a newly-hidden nudge for the current page load.
        // If the plan was already dismissed this load, there is no observable store change.
        if (this.sessionDismissedPlanIds.has(planId)) {
            return false;
        }
        this.sessionDismissedPlanIds.add(planId);
        return true;
    }
    upsertPersistedDismissal(planId) {
        // Remove any existing record so the plan moves to the front with a fresh timestamp.
        this.persistedDismissals = this.persistedDismissals.filter((dismissal)=>dismissal.planId !== planId);
        this.persistedDismissals.unshift({
            planId,
            dismissedAtMs: Date.now()
        });
        this.trimPersistedDismissalsToMaxCount();
        this.persistDismissals();
    }
    hydratePersistedDismissals() {
        const rawPersistedDismissals = this.persistentStorage?.getItem(PlanCalendarNudgeStore.StorageKey);
        if (!rawPersistedDismissals) {
            return [];
        }
        try {
            const parsedDismissals = JSON.parse(rawPersistedDismissals);
            if (!Array.isArray(parsedDismissals)) {
                return [];
            }
            // Keep only valid new-format records; legacy string[] entries and malformed items are dropped.
            return parsedDismissals.filter(PlanCalendarNudgeStore.isPersistedNudgeDismissal);
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1d81c00a /* tag_362ak */ , TraceLevel.Warning, `PlanCalendarNudgeStore hydration parse failure [Error=${error}]`);
            return [];
        }
    }
    /**
     * Snapshots cross-load dismissal eligibility once at hydrate: any persisted dismissal still within the
     * configured window is folded into the in-memory session set, so its state is fixed for this page load.
     */ seedSessionDismissalsFromPersisted() {
        const durationMs = this.configProvider().settings.evoPersistenceSettings.planCalendarNudgeDismissalDurationMs;
        const now = Date.now();
        for (const dismissal of this.persistedDismissals){
            if (now - dismissal.dismissedAtMs < durationMs) {
                this.sessionDismissedPlanIds.add(dismissal.planId);
            }
        }
    }
    trimPersistedDismissalsToMaxCount() {
        const maxDismissedPlanIds = this.configProvider().settings.evoPersistenceSettings.maxPlanCalendarNudgeDismissedPlanIds;
        this.persistedDismissals = this.persistedDismissals.slice(0, maxDismissedPlanIds);
    }
    persistDismissals() {
        this.persistentStorage?.setItem(PlanCalendarNudgeStore.StorageKey, JSON.stringify(this.persistedDismissals));
    }
    static isPersistedNudgeDismissal(value) {
        return typeof value === "object" && value !== null && "planId" in value && "dismissedAtMs" in value && typeof value.planId === "string" && typeof value.dismissedAtMs === "number";
    }
    constructor(loggers, persistentStorage, configProvider){
        super(), this.loggers = loggers, this.persistentStorage = persistentStorage, this.configProvider = configProvider, /** Plan ids dismissed during the current page load. In-memory only; not persisted. */ this.sessionDismissedPlanIds = new Set();
        this.persistedDismissals = this.hydratePersistedDismissals();
        this.seedSessionDismissalsFromPersisted();
    }
}
PlanCalendarNudgeStore.StorageKey = "planCalendarNudgeDismissedPlanIds";
